import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { scrollToSection } from "../../lib/scroll";
import { COMPANY } from "../../data/site";

const LINKS = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "Projects", href: "#projects" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection(href), 400);
    } else {
      scrollToSection(href);
    }
  };

  return (
    <header
      data-testid="main-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "border-b border-black/10 bg-white/90 backdrop-blur-md"
          : "border-b border-black/5 bg-white/70 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6">
        <button
          data-testid="nav-logo"
          onClick={() => go("#home")}
          className="flex items-center gap-3 text-left"
        >
          <img src="/logo.png" alt="Sunny's Construction Ltd" className="h-11 w-auto" />
        </button>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
          {LINKS.map((link) => (
            <button
              key={link.href}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              onClick={() => go(link.href)}
              className="group relative text-sm font-medium text-black/60 transition-colors duration-300 hover:text-ink"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-bronze transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-6 lg:flex">
          <a
            data-testid="nav-phone"
            href={COMPANY.phoneHref}
            className="flex items-center gap-2 text-sm text-black/60 transition-colors duration-300 hover:text-bronze"
          >
            <Phone className="h-4 w-4 text-bronze" />
            {COMPANY.phone}
          </a>
          <button
            data-testid="nav-quote-btn"
            onClick={() => go("#contact")}
            className="bg-bronze px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-ink hover:text-bronze"
          >
            Get a Quote
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="grid h-10 w-10 place-items-center border border-black/15 text-ink lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-black/10 bg-white/95 backdrop-blur-md lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((link) => (
                <button
                  key={link.href}
                  data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                  onClick={() => go(link.href)}
                  className="py-3 text-left font-display text-2xl font-semibold uppercase tracking-wide text-ink/80 transition-colors duration-300 hover:text-bronze"
                >
                  {link.label}
                </button>
              ))}
              <div className="mt-4 flex flex-col gap-4 border-t border-black/10 pt-6">
                <a
                  data-testid="nav-mobile-phone"
                  href={COMPANY.phoneHref}
                  className="flex items-center gap-2 text-sm text-black/60"
                >
                  <Phone className="h-4 w-4 text-bronze" />
                  {COMPANY.phone}
                </a>
                <button
                  data-testid="nav-mobile-quote-btn"
                  onClick={() => go("#contact")}
                  className="bg-bronze px-6 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-ink"
                >
                  Get a Quote
                </button>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
